/**
 * Memory Graph — frozen type contract (G0).
 *
 * This module is the single source of truth the three parallel builders depend
 * on: G1 (data layer), G2 (deterministic layout) and G3 (SVG rendering). It is
 * types plus small const lookup tables only — no runtime logic, no `any`, every
 * field readonly.
 *
 * Data source boundary: the graph is assembled only from the existing eight
 * typed `/v1/knowledge/*` operations (search, fetch, get_entity, get_relations,
 * get_timeline, get_open_commitments, get_evidence, get_source_reference),
 * consumed through the same `QueryEnvelope` pattern as commitments.ts. No new
 * endpoint is introduced.
 *
 * Data honesty (see README and CLAUDE.md): visibility never widens. The model
 * carries no count, silhouette or hint of entities/relations the caller is not
 * authorized to see. Missing/stale/failed coverage yields `status: "unknown"`
 * with an `asOf` watermark — never a silently partial graph presented as final.
 * Derived signals (status badges, clusters, layout) never override or invent
 * evidence; a `danger`/`warning` badge is only ever a projection of an
 * evidence-bound commitment, and an `unknown` commitment never produces a badge.
 */

import type {
  ClaimState,
  CoverageReason,
  EntityType,
  RelationType,
} from "@obsina/domain";
import type { Tone } from "@obsina/design-system";
import type {
  CommitmentStatus,
  CoverageDurability,
  EvidenceChannel,
  KnowledgeCoverage,
  KnowledgeUnknownReason,
} from "../commitments";

/**
 * Re-exported so downstream builders import the graph vocabulary from one place.
 * The unions stay identical to `@obsina/domain`; do not fork them here.
 */
export type { ClaimState, EntityType, RelationType } from "@obsina/domain";

/**
 * Record lifecycle of an entity, mirroring `EntityRecord.status`. A `merged`
 * record was folded into another; a `tombstoned` one was retired. Neither is a
 * live record, and neither may be drawn as one.
 */
export type EntityStatus = "active" | "merged" | "tombstoned";
/** Re-exported so the inspector's evidence channel label lives in one vocabulary. */
export type { EvidenceChannel } from "../commitments";
/** Re-exported so the unknown surface's durability wording has one vocabulary. */
export type { CoverageDurability, KnowledgeCoverage } from "../commitments";

/* ------------------------------------------------------------------------- *
 * Icon vocabulary
 *
 * lucide-react (pinned `lucide-react@1.24.0` in apps/web/package.json) is the
 * only icon source. Values below are lucide *icon names* (kebab-case), runtime
 * free; the README lists the matching PascalCase `lucide-react` component each
 * name resolves to, which G3 imports and renders through
 * `<Icon icon={Component} />` from `@obsina/design-system`.
 * ------------------------------------------------------------------------- */

/**
 * Domain entity type → lucide icon name. The eight types the P4 prototype drew
 * keep its exact icons; `order`, `mail_thread` and `source_item` (present in the
 * domain but absent from the prototype) are assigned here and justified in the
 * README. There is no `commitment` entity type — commitments surface as status
 * badges and inspector open items, never as their own node.
 */
export const ENTITY_ICON_NAME: Record<EntityType, string> = {
  person: "user",
  company: "building-2",
  project: "box",
  quote: "receipt-text",
  order: "clipboard-list",
  invoice: "receipt",
  contract: "file-signature",
  document: "file-text",
  mailbox: "mail",
  mail_thread: "messages-square",
  source_item: "file",
  meeting: "calendar-clock",
} as const;

/**
 * Presentational classification of an evidence excerpt, used only to pick a row
 * icon in the inspector. Derived best-effort by G1 from the source item kind /
 * linked entity type / locator identifier type; when it cannot be determined it
 * falls back to `source` — it is never invented to imply a stronger claim.
 */
export type EvidenceKind =
  "mail" | "contract" | "invoice" | "quote" | "order" | "document" | "source";

/**
 * The email header line an evidence quote was captured from, when the quote is
 * recognizably one. Presentation only: it decides how the inspector reads a
 * transport artefact back to a person, and never changes the quote itself.
 */
export type EmailHeaderField =
  "to" | "from" | "cc" | "bcc" | "replyTo" | "subject" | "date" | "sent";

/** Evidence kind → lucide icon name (aligned with {@link ENTITY_ICON_NAME}). */
export const EVIDENCE_KIND_ICON_NAME: Record<EvidenceKind, string> = {
  mail: "mail",
  contract: "file-signature",
  invoice: "receipt",
  quote: "receipt-text",
  order: "clipboard-list",
  document: "file-text",
  source: "file",
} as const;

/* ------------------------------------------------------------------------- *
 * Color tokens
 *
 * Colors are always design-system semantic token names, never raw hex. Each
 * value below is a member of the design-system `Tone` union and resolves to an
 * `--obsina-*` custom property at render time (G3 uses `toneProperties(tone)`
 * or the corresponding marker/soft variable). The two `_*IsTone` type aliases
 * are compile-time guards: if a token drifts from the design system, this file
 * stops compiling.
 * ------------------------------------------------------------------------- */

type AssertExtends<T extends U, U> = T;

/**
 * Cluster colour. Grouping only — it carries NO status meaning and is assigned
 * by a deterministic, non-semantic rule (see {@link CLUSTER_COLOR_ORDER} and
 * the derivation contract in the README). `danger` is deliberately excluded so
 * a cluster never reads as a permanent alert. Used by the narrow-screen cluster
 * list; the map itself no longer tints regions by cluster.
 */
export type ClusterColorToken = "accent" | "info" | "success" | "warning";
type _ClusterColorTokenIsTone = AssertExtends<ClusterColorToken, Tone>;

/** Node ring tint, keyed by entity type. `danger` is reserved for status. */
export type RingColorToken = "info" | "neutral" | "warning" | "success";
type _RingColorTokenIsTone = AssertExtends<RingColorToken, Tone>;

/**
 * Entity type → node ring token. Matches the prototype `TYPES.ring` mapping for
 * the eight drawn types; the three domain-only additions are justified in the
 * README.
 */
export const ENTITY_RING_TOKEN: Record<EntityType, RingColorToken> = {
  person: "info",
  company: "neutral",
  project: "warning",
  quote: "warning",
  order: "warning",
  invoice: "warning",
  contract: "neutral",
  document: "success",
  mailbox: "info",
  mail_thread: "info",
  source_item: "neutral",
  meeting: "info",
} as const;

/**
 * Fixed palette order for the non-semantic cluster color assignment. Clusters,
 * ordered by (hub degree desc, hub entityId asc), take tokens cycling through
 * this list so colors are stable across renders and independent of entity
 * meaning.
 */
export const CLUSTER_COLOR_ORDER: readonly ClusterColorToken[] = [
  "accent",
  "info",
  "success",
  "warning",
] as const;

/* ------------------------------------------------------------------------- *
 * Status badges (derived from evidence-bound commitments)
 * ------------------------------------------------------------------------- */

/** Only alerting tones exist; an `unknown` commitment never yields a badge. */
export type NodeStatusTone = "danger" | "warning";

/**
 * Typed, localizable status badge. `overdue` mirrors the P1 definition of an
 * overdue commitment (a certain deadline passed under complete coverage) and
 * carries whole days past due at `asOf`; `dueSoon` carries the ISO due instant
 * for the nearest open commitment falling within {@link NODE_STATUS_DUE_SOON_DAYS}.
 * The label itself is produced by G4 from {@link MemoryGraphMessages}; this
 * model never holds display strings.
 */
export type NodeStatusBadge =
  | {
      readonly tone: "danger";
      readonly kind: "overdue";
      readonly overdueDays: number;
    }
  | {
      readonly tone: "warning";
      readonly kind: "dueSoon";
      readonly dueAt: string;
    };

/** Window (days, relative to `asOf`) within which an upcoming due date warns. */
export const NODE_STATUS_DUE_SOON_DAYS = 7 as const;

/* ------------------------------------------------------------------------- *
 * Node size tiers
 * ------------------------------------------------------------------------- */

export const NODE_SIZE_TIERS = ["hub", "major", "regular", "minor"] as const;
export type NodeSizeTier = (typeof NODE_SIZE_TIERS)[number];

/**
 * Base node radius per tier, expressed as a fraction of the layout reference
 * width ({@link LAYOUT_REFERENCE}). G2 echoes this on each {@link LayoutNode};
 * G3 multiplies by the rendered stage width. (≈ 43 / 30 / 22 / 16 px on the
 * 1440-wide reference stage, matching the prototype radius range.)
 */
export const NODE_RADIUS_BY_TIER: Record<NodeSizeTier, number> = {
  hub: 0.03,
  major: 0.021,
  regular: 0.015,
  minor: 0.011,
} as const;

/**
 * Degree (distinct connected nodes) at or above which a node is at least the
 * given tier. G1 assigns the highest tier whose threshold the degree meets;
 * cluster hubs are always `hub` regardless. Deterministic and shared so G1's
 * tiering and G2's radius sizing never disagree.
 */
export const NODE_SIZE_TIER_MIN_DEGREE: Record<
  Exclude<NodeSizeTier, "minor">,
  number
> = {
  hub: 9,
  major: 5,
  regular: 2,
} as const;

/* ------------------------------------------------------------------------- *
 * Graph data model (produced by G1)
 * ------------------------------------------------------------------------- */

/** A graph node is exactly one authorized domain entity. */
/**
 * One address (or handle) an entity was recognised by, as the read surface
 * released it. It exists so two entities that share a name can be told apart on
 * screen — it is never evidence that two entities are the same person, which is
 * a claim Obsina does not make.
 */
export interface EntityIdentifier {
  readonly kind: "email_address";
  readonly value: string;
}

export interface GraphNode {
  /** Server entity id (`entityId`); stable identity across the whole contract. */
  readonly id: string;
  readonly entityType: EntityType;
  /** Human-readable label resolved from the entity; never an invented value. */
  readonly label: string;
  /**
   * What distinguishes this record from another of the same name, when the
   * surface carries one. Absent means the build or the record has none — never
   * rendered as an empty line.
   */
  readonly identifier?: EntityIdentifier;
  /** How many identifiers the entity holds; lets one of several say so. */
  readonly identifierCount?: number;
  /** Count of authorized evidence blocks backing this entity. */
  readonly evidenceCount: number;
  /** Distinct authorized neighbors (graph degree); drives tier and edge weight. */
  readonly linkCount: number;
  readonly sizeTier: NodeSizeTier;
  /** Cluster this node was deterministically assigned to. */
  readonly clusterId: string;
  /** True when the entity first appeared in the most recent completed sync. */
  readonly isNew: boolean;
  /**
   * Record lifecycle as the API stated it (`EntityRecord.status`). A merged or
   * tombstoned entity is still drawn — dropping it would hide a record the
   * system holds — but it may never look like an ordinary one. Optional for the
   * same reason as {@link GraphEdge.states}: absent is "not read", not "active".
   */
  readonly status?: EntityStatus;
  /** Aggregate (worst) alert for the entity's open commitments, or null. */
  readonly statusBadge: NodeStatusBadge | null;
}

/**
 * An undirected relation between two nodes. At most one edge exists per
 * unordered node pair: G1 merges parallel relations, summing weight and marking
 * the edge `strong` if any underlying relation is labeled. `source`/`target`
 * preserve the domain relation's subject→object direction for dedup and future
 * use; the prototype renders edges without arrowheads.
 */
export interface GraphEdge {
  readonly sourceNodeId: string;
  readonly targetNodeId: string;
  /**
   * Vocabulary relation, or null when the record states a kind this build does
   * not carry. Null is never "there is no relation": the edge is real,
   * authorized and evidenced, and only its *name* is missing — the surfaces say
   * exactly that ("Linked", "is linked to") instead of guessing a verb or
   * printing the raw code. The data layer produces null for that reason alone;
   * it draws no synthetic structural links.
   */
  readonly relationType: RelationType | null;
  /**
   * How strongly the record ties these two nodes; G3 maps it to stroke width
   * and opacity. Preferably the stored bond strength — how many distinct
   * published documents attest the bond, which is stable under re-derivation —
   * and the backing evidence count when the API states no strength for every
   * relation in the pair. Never mixed: a number that is part strength and part
   * evidence count would mean neither.
   */
  readonly weight: number;
  /** Labeled / emphasized relation (vs. a thin auto hub link). */
  readonly strong: boolean;
  /**
   * Distinct assertion states of the relations merged into this edge, as the
   * API stated them (`RelationRecord.state`). Deliberately a set, not one
   * winning value: an edge merges parallel relations, and choosing a single
   * "worst" state would decide a question the record never asked. The view
   * draws the most severe present and can still say the edge is not uniform.
   *
   * Optional because the parser does not carry it yet. Absent means *the
   * responder was never read for it* — it is not a claim of `supported`, and
   * nothing may render it as one; it renders exactly as today until the parser
   * fills it in.
   */
  readonly states?: readonly ClaimState[];
}

/**
 * A deterministically derived cluster. Membership and hub come from the
 * non-semantic rule described in the README. It names the cluster in the
 * narrow-screen list; the map no longer paints it (see `computeGraphLayout`).
 */
export interface GraphCluster {
  readonly id: string;
  /** Label sourced from the hub entity's label. */
  readonly label: string;
  /** The highest-degree member; the cluster's visual and structural center. */
  readonly hubNodeId: string;
  readonly memberNodeIds: readonly string[];
  readonly colorToken: ClusterColorToken;
}

/**
 * Upper bound on nodes returned for a single graph view (bounded fetch). When
 * more authorized nodes exist than this cap, the result is trimmed to the most
 * connected ones and {@link ResolvedMemoryGraph.truncated} is set.
 */
export const MEMORY_GRAPH_MAX_NODES = 240 as const;

/**
 * Upper bound on raw relations pulled for the full-network view. The graph draws
 * an edge only between two resolved nodes (buildEdges drops any relation whose
 * endpoint is outside the node cap), so this bounds the enumeration cost while
 * staying well above {@link MEMORY_GRAPH_MAX_NODES}. Exceeding it (or the node
 * cap) sets {@link ResolvedMemoryGraph.truncated} — never a silent trim.
 */
export const MEMORY_GRAPH_MAX_EDGES = 600 as const;

/** Upper bound on derived clusters; see the README cluster-count formula. */
export const MEMORY_GRAPH_MAX_CLUSTERS = 8 as const;

/** Minimum degree for a node to be eligible as a cluster hub. */
export const CLUSTER_MIN_HUB_DEGREE = 3 as const;

/**
 * Message key for a coverage durability plus the case where the control plane
 * stated none. `unstated` is not a fifth durability: it is the absence of the
 * claim, and the copy behind it must therefore promise nothing about waiting.
 */
export type CoverageDurabilityKey = CoverageDurability | "unstated";

/** What every graph view carries, resolved or not. */
interface MemoryGraphEnvelope {
  /** Coverage watermark for the whole view; null only when unestablished. */
  readonly asOf: string | null;
  readonly coverage: KnowledgeCoverage;
}

/**
 * A resolved view. Every count below is exact and authorized: `nodes.length`
 * and `edges.length` are the network's size at `asOf`.
 *
 * `truncated: true` means only that more *authorized* nodes exist than the cap —
 * it never quantifies or hints at unauthorized data.
 */
export interface ResolvedMemoryGraph extends MemoryGraphEnvelope {
  readonly status: "ok";
  readonly reason: null;
  readonly nodes: readonly GraphNode[];
  readonly edges: readonly GraphEdge[];
  readonly clusters: readonly GraphCluster[];
  readonly truncated: boolean;
}

/**
 * An unknown view — deliberately count-free.
 *
 * `nodes`, `edges`, `clusters` and `truncated` do not exist on this variant, so
 * a surface physically cannot read a size out of an unresolved answer. That is
 * the whole point: the field this type used to carry was an empty array, and
 * every reader that rendered `nodes.length` turned "we have not read it" into a
 * confident "0 entities". Missing coverage is not an answer of zero (CLAUDE.md);
 * with no count to read, the only thing left to render is the unknown itself.
 */
export interface UnknownMemoryGraph extends MemoryGraphEnvelope {
  readonly status: "unknown";
  readonly reason: KnowledgeUnknownReason | null;
}

/** The complete, honesty-preserving envelope for a graph view. */
export type MemoryGraphData = ResolvedMemoryGraph | UnknownMemoryGraph;

/* ------------------------------------------------------------------------- *
 * Layout (produced by G2)
 *
 * The layout is a pure, deterministic function of its input: identical
 * LayoutInput (including `seed` and `viewport`) always yields identical
 * LayoutResult. No `Math.random`, no wall clock. All geometry is normalized to
 * the unit box [0,1] × [0,1] laid out at the reference aspect; G3 multiplies by
 * the rendered stage size.
 * ------------------------------------------------------------------------- */

/**
 * Reference stage the normalized geometry is proportioned against (from the P4
 * prototype). Downstream code scales freely; only the aspect ratio is binding.
 */
export const LAYOUT_REFERENCE = {
  width: 1440,
  height: 840,
  aspect: 1440 / 840,
} as const;

/**
 * Zoom scale bounds and defaults.
 *
 * The prototype's 0.7–1.8 band was drawn against eight mock nodes, where the
 * whole map was legible at rest and zoom was a nicety. On a real tenant the
 * scale is what separates a crowded neighbourhood — and 1.8× cannot separate
 * anything: names are laid out at a fixed size *inside* the scaled scene, so
 * scaling the scene scaled the text with it and the same words stayed on top of
 * each other, only larger. The band is now wide enough for the map to be read
 * both whole (0.4) and up close (4), and the labels hold their own screen size
 * across it (see `--mg-zoom` in GraphView/memory-graph.css) so zooming in
 * genuinely thins the page out.
 *
 * `factor` replaces the old additive `step`: a fixed +0.15 is a third of the
 * view at 0.4 and a rounding error at 4. A ratio is the same gesture everywhere.
 */
export const GRAPH_ZOOM = {
  min: 0.4,
  max: 4,
  factor: 1.25,
  overviewDefault: 1,
  focusDefault: 1.04,
} as const;

/** Rendered stage size in CSS pixels; only the ratio needs to match reference. */
export interface Viewport {
  readonly width: number;
  readonly height: number;
}

/** A point normalized to the unit layout box; each component in [0, 1]. */
export interface NormalizedPoint {
  readonly x: number;
  readonly y: number;
}

export interface LayoutInput {
  readonly nodes: readonly GraphNode[];
  readonly edges: readonly GraphEdge[];
  readonly clusters: readonly GraphCluster[];
  /** Deterministic seed; equal seeds reproduce the layout exactly. */
  readonly seed: number;
  readonly viewport: Viewport;
}

export interface LayoutNode {
  readonly nodeId: string;
  readonly position: NormalizedPoint;
  /** Normalized radius (fraction of stage width); echoes NODE_RADIUS_BY_TIER. */
  readonly radius: number;
  readonly sizeTier: NodeSizeTier;
}

/**
 * A drawable edge: a single quadratic Bézier from `start` to `end` bending
 * through `control` (endpoints at node centers, as in the prototype).
 * `labelAnchor` is the point at which a strong edge's relation label sits, and
 * is null for unlabeled edges.
 */
export interface LayoutEdge {
  readonly sourceNodeId: string;
  readonly targetNodeId: string;
  readonly start: NormalizedPoint;
  readonly end: NormalizedPoint;
  readonly control: NormalizedPoint;
  readonly labelAnchor: NormalizedPoint | null;
  readonly weight: number;
  readonly strong: boolean;
  readonly relationType: RelationType | null;
  /** Carried through from {@link GraphEdge.states}; see the note there. */
  readonly states?: readonly ClaimState[];
}

/** One minimap dot; G3 sizes it from `sizeTier`. */
export interface MinimapDot {
  readonly nodeId: string;
  readonly position: NormalizedPoint;
  readonly sizeTier: NodeSizeTier;
}

export interface LayoutResult {
  readonly nodes: readonly LayoutNode[];
  readonly edges: readonly LayoutEdge[];
  readonly minimap: readonly MinimapDot[];
  /** Echoed from the input for provenance/determinism checks. */
  readonly seed: number;
}

/* ------------------------------------------------------------------------- *
 * Interaction state and per-element render state (G3)
 * ------------------------------------------------------------------------- */

export interface GraphInteractionState {
  /** The pinned/inspected node, or null in overview. */
  readonly focusedNodeId: string | null;
  readonly hoveredNodeId: string | null;
  /** Roving keyboard focus target for the node layer. */
  readonly keyboardFocusedNodeId: string | null;
  /** Current zoom scale, clamped to [GRAPH_ZOOM.min, GRAPH_ZOOM.max]. */
  readonly zoom: number;
}

/** Derived visual state of a node under the current interaction. */
export type NodeRenderState = "default" | "focused" | "neighbor" | "faded";
/** Derived visual state of an edge under the current interaction. */
export type EdgeRenderState = "default" | "active" | "hot" | "faded";

/* ------------------------------------------------------------------------- *
 * Inspector (focused-node detail; built by G1, rendered by G3)
 *
 * There is no "Ask about this entity" affordance: the MVP web has no Ask
 * surface, and a dead control is forbidden.
 * ------------------------------------------------------------------------- */

/**
 * One open commitment involving the focused entity.
 *
 * There is deliberately no reference code: the typed ops expose no business
 * reference (e.g. `CON-333`), and the shortened commitment UUID that once stood
 * in for one told a reader nothing while reading as if it meant something. An
 * opaque internal id is not evidence, so dropping it narrows no provenance.
 */
export interface InspectorOpenItem {
  /** Commitment summary text. */
  readonly text: string;
  readonly ownerLabel: string | null;
  /**
   * Who the promise was made *to*, when the commitment record names a
   * counterparty. Null is a real answer here and is shown as one: a promise
   * with no recorded recipient is common (a plan stated to no one in
   * particular), and inventing a recipient — or quietly omitting the question —
   * would both misread the record.
   */
  readonly counterpartyLabel: string | null;
  /** ISO due instant; G4 formats it. Null when no certain deadline exists. */
  readonly dueDate: string | null;
  /** Underlying commitment status, preserved so `unknown` is shown honestly. */
  readonly commitmentStatus: CommitmentStatus;
  /** Alert spill; null for `open`/`unknown` items with no near/overdue date. */
  readonly badge: NodeStatusBadge | null;
}

/** One evidence excerpt shown in the inspector. */
export interface InspectorEvidenceRow {
  readonly quote: string;
  /** ISO occurrence instant; G4 formats it. Null when unknown. */
  readonly occurredAt: string | null;
  readonly kind: EvidenceKind;
  /**
   * The authorized source channel this excerpt was drawn from (mail / calendar /
   * Teams), or null when the API resolved no channel or one without a chip. It is
   * only a type label on an already-authorized source, so it widens no
   * visibility; it is distinct from `kind`, which is a best-effort content-shape
   * icon, not the channel.
   */
  readonly sourceChannel: EvidenceChannel | null;
  /** Evidence pointer for the "Open evidence" affordance. */
  readonly evidenceId: string;
  /**
   * True when the verbatim quote is an email header line (e.g. "To:", "Subject:").
   * The inspector labels such rows so a raw header dump reads as a header rather
   * than ordinary prose. The quote is always shown verbatim, never rewritten.
   * The evidence "title" field is deliberately not carried here: the API
   * coalesces a real document title with an opaque provider item id and the two
   * are indistinguishable client-side, so BOTH are conservatively dropped
   * (never rendered, never in the accessibility tree) rather than ever showing
   * an opaque id as a source label. Surfacing real titles needs the API to mark
   * the fallback separately — recorded as a follow-up.
   */
  readonly isHeaderLine: boolean;
  /**
   * True when this excerpt is an OCR-derived reading of an image rather than the
   * provider's own text. The inspector marks such rows with a labelled badge —
   * text and icon, not colour alone — so a person can see the quote is a machine
   * reading before trusting a figure in it. The quote itself is always shown
   * verbatim; the label narrows trust, it never hides content.
   */
  readonly isDerived: boolean;
}

export interface InspectorModel {
  readonly entityId: string;
  /** Coverage watermark for this entity's answer; null when unestablished. */
  readonly asOf: string | null;
  readonly name: string;
  readonly entityType: EntityType;
  readonly evidenceCount: number;
  readonly openItems: readonly InspectorOpenItem[];
  /** A bounded first page of evidence rows. */
  readonly evidenceRows: readonly InspectorEvidenceRow[];
  /**
   * True while the excerpt page is still being read. The rest of the panel is
   * answerable one round trip earlier than the excerpts are — the excerpt ids
   * only exist once the entity itself has answered — so the model is handed over
   * before them. An empty `evidenceRows` under this flag means "not read yet",
   * which is not the same statement as "nothing supports this", and no count may
   * be derived from it.
   */
  readonly evidencePending: boolean;
  /** Total authorized evidence blocks, for "View all {n} evidence blocks". */
  readonly viewAllCount: number;
  /**
   * True when {@link evidenceCount} and {@link viewAllCount} are lower bounds:
   * the entity holds more authorized evidence than one record may carry. Every
   * count derived from them must then read as "n+", never as a total.
   */
  readonly evidenceTruncated: boolean;
}

/* ------------------------------------------------------------------------- *
 * Narrow (320px) cluster-list fallback (built by G1/G2, rendered by G3)
 *
 * The honest small-screen surface: the full canvas is replaced by a cluster
 * list, each expandable to its member entities.
 * ------------------------------------------------------------------------- */

/** One decorative-but-faithful dot in a cluster card vignette. */
export interface NarrowVignetteDot {
  /** Member position re-normalized into the vignette's local [0,1] box. */
  readonly position: NormalizedPoint;
  readonly sizeTier: NodeSizeTier;
  /** True for the cluster hub, which the vignette emphasizes. */
  readonly isHub: boolean;
}

/** One member row inside an expanded cluster card. */
export interface NarrowClusterEntity {
  readonly nodeId: string;
  readonly name: string;
  readonly entityType: EntityType;
  readonly linkCount: number;
  readonly statusBadge: NodeStatusBadge | null;
}

export interface NarrowClusterCard {
  readonly clusterId: string;
  readonly label: string;
  readonly entityCount: number;
  readonly evidenceCount: number;
  readonly colorToken: ClusterColorToken;
  /** Miniature scatter of member nodes for the card vignette. */
  readonly vignette: readonly NarrowVignetteDot[];
  /** Member rows, revealed when the card is expanded. */
  readonly entities: readonly NarrowClusterEntity[];
}

/* ------------------------------------------------------------------------- *
 * Messages
 *
 * Typed interface for every user-visible string in the Memory Graph. G3
 * components receive `messages: MemoryGraphMessages` via props; G4 wires the
 * concrete values in apps/web/src/messages.ts (as `enMessages.memoryGraph`,
 * `satisfies MemoryGraphMessages`), reusing shared copy (as-of, coverage
 * reasons, unknown/unavailable/unauthorized bodies) where it already exists.
 * Interpolated strings are function-valued, matching the existing messages.ts
 * pattern. No string literal lives in a G3 component.
 * ------------------------------------------------------------------------- */

export interface MemoryGraphMessages {
  /** Primary-nav label for the view. */
  readonly nav: string;
  readonly title: string;
  readonly body: string;
  readonly loading: string;

  readonly search: {
    readonly placeholder: string;
    /** Accessible label for the search input. */
    readonly label: string;
    /** Decorative keyboard shortcut hint (e.g. `⌘K`). */
    readonly shortcut: string;
    /** Accessible label for the clear control that replaces the hint while typing. */
    readonly clear: string;
    /**
     * Stated when a query matches no drawn entity. Silence here reads as "the
     * map has no such entity" *and* leaves the previous focus standing, so the
     * miss is said out loud instead.
     */
    readonly noMatch: string;
  };

  readonly stage: {
    /** Bottom-strip as-of, e.g. "Graph as of {time} (last completed sync)". */
    readonly asOf: (time: string) => string;
    readonly asOfUnknown: string;
    /** Bottom-strip authorization note (the lock line). */
    readonly authorizationNote: string;
    /** Honest bounded-fetch notice; states a subset of authorized entities. */
    readonly truncated: (shownCount: number) => string;
  };

  /**
   * Honest full-network scale line. `complete` is used when every authorized
   * node/edge is shown (the count is exact); `truncated` when the view is bounded
   * and more authorized data exists — never a silent trim, and never a count of
   * anything the viewer is not authorized to see.
   *
   * `unknown` replaces both when the answer is unresolved. It carries no number
   * at all — an unresolved network has no size to state — and instead says why
   * the size is not known, keyed by {@link CoverageDurabilityKey} so a terminal
   * gap never implies that waiting will fill it. Status is in the words, not a
   * color.
   */
  readonly scale: {
    readonly complete: (entities: number, connections: number) => string;
    readonly truncated: (entities: number, connections: number) => string;
    /**
     * Same exact counts as `complete`/`truncated`, but under `partial` coverage:
     * every node drawn is real and authorized, so the number stands — the line
     * simply may not call the network "full" while a derived layer over it is
     * unread. What is unread is stated by {@link MemoryGraphMessages.partial}.
     */
    readonly partial: (entities: number, connections: number) => string;
    readonly partialTruncated: (
      entities: number,
      connections: number,
    ) => string;
    readonly unknown: Record<CoverageDurabilityKey, string>;
  };

  readonly zoom: {
    readonly zoomIn: string;
    readonly zoomOut: string;
    readonly fit: string;
  };
  /** Entity type badge labels. */
  readonly entityType: Record<EntityType, string>;
  /**
   * Human wording for each relation in the frozen vocabulary, read left-to-right
   * from the edge's source to its target ("Emailed", not `sent_to`). The raw
   * vocabulary code is a database term: it names the relation truthfully but
   * tells a reader nothing, and 62 copies of it are what made the map
   * unreadable. A code outside this map still falls back to the raw term rather
   * than being dropped — an unnamed edge would be a silent omission.
   */
  readonly relationType: Record<RelationType, string>;
  /**
   * The same vocabulary as a verb phrase, so a relation can be said as a
   * sentence rather than named as a kind: "Nordwind GmbH **emailed** Miray
   * Aksoy". Read source → target, exactly like {@link relationType}.
   *
   * A two-word chip on a line answers *what kind of link is this*; it does not
   * answer *what does this line mean*, which is the question a reader looking at
   * two circles and a curve actually has. Both exist because both are needed:
   * the chip fits on the map, the sentence fits under it.
   */
  readonly relationVerb: Record<RelationType, string>;
  /** Verb for a relation this build's vocabulary does not carry. */
  readonly relationVerbUnknown: string;
  /** Assembles the sentence, so word order stays translatable. */
  readonly relationSentence: (
    subject: string,
    verb: string,
    object: string,
  ) => string;
  /**
   * The hover card under the map: what the circle or line the pointer is on
   * actually is, said in sentences a reader has never had to learn.
   *
   * Every line here is a statement about what *this system* holds, never about
   * the world: `evidenceNone` says nothing you can open backs it yet, not that
   * nothing does, and `asOfUnknown` says the watermark is unstated rather than
   * quietly omitting it.
   */
  readonly peek: {
    readonly evidence: (count: number) => string;
    readonly evidenceNone: string;
    readonly links: (count: number) => string;
    readonly linksNone: string;
    /** Trailing "and N more" when the card states only the strongest links. */
    readonly more: (count: number) => string;
    /** How far the reading behind the map got, said where the reader is looking. */
    readonly asOf: (time: string) => string;
    readonly asOfUnknown: string;
    /** What one click gets: the sources, not another summary. */
    readonly open: string;
  };
  /** Accessible labels for evidence row icons. */
  /**
   * Trust wording for a relation's assertion state. `supported` is deliberately
   * null: the ordinary case carries no words, so the exceptions stay loud.
   */
  readonly claimState: Record<ClaimState, string | null>;
  /** Trust wording for an entity's record lifecycle; `active` carries none. */
  readonly entityStatus: Record<EntityStatus, string | null>;
  /** Stage-strip sentence stating that some relations rest on disagreement. */
  readonly conflictNotice: (relations: number) => string;
  readonly evidenceKind: Record<EvidenceKind, string>;
  /** Channel-type chip labels for an evidence row's source channel. */
  readonly evidenceChannel: Record<EvidenceChannel, string>;

  readonly badge: {
    /** Danger badge, e.g. "Overdue {days}d". */
    readonly overdue: (days: number) => string;
    /** Warning badge, e.g. "Due {date}". */
    readonly dueSoon: (date: string) => string;
  };

  readonly inspector: {
    readonly asOf: (time: string) => string;
    readonly asOfUnknown: string;
    /** Heading over the focused entity's neighbours, named rather than counted. */
    readonly connectionsSection: string;
    /** Stated when the drawn map holds no link for this entity. */
    readonly connectionsEmpty: (name: string) => string;
    /**
     * Stated under the named list whenever the map itself is bounded: a list of
     * names reads as the whole list, and on a bounded map it is not.
     */
    readonly connectionsBounded: string;
    /**
     * Kind label over the two best-evidenced names while the rest are folded
     * away, e.g. "Most often". It names what the folded row is showing, in the
     * same slot the relation kind occupies in the open list.
     */
    readonly connectionsLead: string;
    /** Opens the folded list, e.g. "9 more connections". */
    readonly connectionsRest: (count: number) => string;
    /** Folds the list back up. */
    readonly connectionsCollapse: string;
    /** Group label for an edge the relation vocabulary does not name. */
    readonly connectionUnlabeled: string;
    /** Accessible label for the control that moves the map to a neighbour. */
    readonly connectionFocus: (name: string) => string;
    /**
     * A neighbour's name and the address that record was recognised by, as one
     * sentence. It is what a screen reader hears for the chip, and it is what
     * lets two records of the same name be told apart without either of them
     * claiming to be the other.
     */
    readonly connectionNamed: (name: string, identifier: string) => string;
    /** Said in place of a name when the record's only name is its own address. */
    readonly connectionUnnamed: string;
    /** One address of several, so a single one is never implied to be the only one. */
    readonly connectionIdentifierOfMany: (
      identifier: string,
      total: number,
    ) => string;
    /** Heading when the promises below belong to more than one promiser. */
    readonly openItemsSection: string;
    /**
     * Heading when every promise below is the same person's, e.g. "What Miray
     * Aksoy promised" — said once here instead of on every row.
     */
    readonly openItemsBy: (owner: string) => string;
    /**
     * Heading when the promises below share one promiser *and* one recipient,
     * e.g. "What Miray Aksoy promised Nordwind GmbH" — said once here instead
     * of a recipient line under every row.
     */
    readonly openItemsByTo: (owner: string, recipient: string) => string;
    readonly evidenceSection: string;
    readonly emptyOpenItems: (name: string) => string;
    readonly emptyEvidence: string;
    /**
     * Said while the excerpt page is still in flight. It exists because the
     * empty sentence beside it makes a claim — that nothing supports this — and
     * an unfinished read may not make it.
     */
    readonly evidenceReading: string;
    /** e.g. "Due 12 Jul 2026"; omitted when a `dueSoon` badge already says it. */
    readonly openItemDue: (due: string) => string;
    /**
     * Stated when no certain deadline exists — never silently dropped. It is a
     * statement about this system's confidence, not about the source: an
     * unstated deadline and one that could not be resolved are indistinguishable
     * here, so the copy may not claim the source gave none.
     */
    readonly openItemDueUnknown: string;
    /** e.g. "Miray Aksoy promised this"; only when the heading names no owner. */
    readonly openItemOwner: (owner: string) => string;
    readonly openItemCounterparty: (name: string) => string;
    readonly openItemCounterpartyUnknown: string;
    /** Label for an evidence quote taken from an email's address/subject lines. */
    readonly evidenceHeaderLine: string;
    /**
     * The header-line group's own lead, e.g. "Named on 5 email header lines".
     * A recipient list captured verbatim binds the entity to a real authorized
     * source, but reading five of them tells nobody anything the count does not.
     * The group states what they are and how many, and {@link
     * evidenceHeaderGroupShow} reveals every line unchanged — the excerpts are
     * folded, never dropped.
     */
    readonly evidenceHeaderGroup: (count: number) => string;
    /** Date span the folded header lines cover, e.g. "Jul 16 – Jul 27, 2026". */
    readonly evidenceHeaderGroupRange: (from: string, to: string) => string;
    readonly evidenceHeaderGroupShow: string;
    readonly evidenceHeaderGroupHide: string;
    /**
     * What one address- or subject-line mention actually says, as a sentence.
     *
     * A single captured header line used to be printed exactly as the extractor
     * found it — `- Cc: Zahide Öztürk <zahide@…>, Funda Özlü <funda@…>,
     * office@festavolo.eu <office@festavolo.eu>` — under the heading "where does
     * this come from". That is a transport artefact, not an answer: it is the
     * mail system's own bookkeeping, punctuation and all, and the one fact
     * inside it (this address appeared on a message alongside two named people)
     * has to be read out of it by the reader.
     *
     * These say that fact instead. Every part is read off the quote itself —
     * the field it came from, the date the record carries, the other parties
     * named on the same line — so this remains a presentation of the evidence,
     * never a summary of it. The verbatim line stays one activation away
     * ({@link evidenceHeaderGroupShow}); nothing is dropped or reworded.
     */
    readonly evidenceHeaderFieldName: Readonly<
      Record<EmailHeaderField, string>
    >;
    /** e.g. "22 Jul 2026 · named on a message's Cc line, with A and B". */
    readonly evidenceHeaderMention: (
      date: string,
      field: string,
      others: string | null,
    ) => string;
    /** e.g. "22 Jul 2026 · named on a message titled “Revised quote”". */
    readonly evidenceHeaderMentionSubject: (
      date: string,
      subject: string,
    ) => string;
    /**
     * The other parties on the line, read as a list ("A and B", "A, B and C").
     * `overflow` is how many more the line named beyond those spelled out — a
     * bound, so a long recipient list never becomes a long sentence.
     */
    readonly evidenceHeaderOthers: (
      names: readonly string[],
      overflow: number,
    ) => string;
    /** Chip label marking an OCR-derived (machine-read) evidence excerpt. */
    readonly evidenceDerived: string;
    /** Accessible description of the derived-evidence chip for screen readers. */
    readonly evidenceDerivedHint: string;
    /** Shown in place of the date when an evidence excerpt has no known instant. */
    readonly evidenceDateUnknown: string;
    readonly openEvidence: string;
    /**
     * States that the evidence list is a bounded page and says which page, e.g.
     * "Showing the first 4 of 41" — a bare "4 of 41" left the reader asking
     * where the other 37 were. Rendered only when some authorized excerpts are
     * not on screen, so a complete list carries no line at all.
     */
    readonly evidenceShown: (shown: number, total: number) => string;
    /**
     * The same line when the total is itself a lower bound, e.g. "Showing the
     * first 4 of 100 or more".
     */
    readonly evidenceShownTruncated: (shown: number, total: number) => string;
    /** Accessible label for the panel's close control. */
    readonly close: string;
  };

  readonly narrow: {
    readonly info: string;
    readonly searchPlaceholder: string;
    readonly clustersTitle: string;
    /** e.g. "{entities} entities · {evidence} evidence". */
    readonly clusterSub: (entities: number, evidence: number) => string;
    /** e.g. "{type} · {links} links". */
    readonly entitySub: (type: string, links: number) => string;
    readonly expand: (name: string) => string;
    readonly collapse: (name: string) => string;
  };

  readonly empty: {
    readonly title: string;
    readonly body: string;
  };
  /**
   * Copy for a `partial` answer — a drawn map whose recall is bounded. It is
   * deliberately separate from {@link MemoryGraphMessages.unknown}: that surface
   * replaces the map, this one accompanies it. `lead` is keyed by
   * {@link CoverageDurabilityKey} so a terminal gap never reads as a queue that
   * is about to drain, and the two count lines state how far the derived
   * readings behind the map have come.
   */
  readonly partial: {
    /**
     * The clause the strip always carries: that recall is bounded, and whether
     * the bound moves. Short because the strip is one line and this is the half
     * of {@link lead} a reader needs at a glance; `lead` itself is the first
     * line of the detail, not a second copy of this.
     */
    readonly summary: Record<CoverageDurabilityKey, string>;
    readonly lead: Record<CoverageDurabilityKey, string>;
    readonly media: (read: number, total: number) => string;
    readonly knowledge: (read: number, total: number) => string;
    /** Label of the control that reveals the detail, and its accessible name. */
    readonly detailToggle: string;
    readonly detailLabel: string;
    /** For a reason token this build has no sentence for. Never the token. */
    readonly unnamedGap: string;
  };
  readonly unknown: {
    readonly title: string;
    /** Keyed by {@link CoverageDurabilityKey}; see `unknownCoverageBody`. */
    readonly coverageBody: Record<CoverageDurabilityKey, string>;
    readonly evidenceBody: string;
    /** e.g. "{reconciled} of {required} required sources reconciled". */
    readonly coverageProgress: (reconciled: string, required: string) => string;
    readonly reasonsTitle: string;
    /**
     * Total over the closed vocabulary on purpose. A partial `Record<string,
     * string>` let `mail_folder_scope_limited` reach the strip as itself when
     * the API added it — the catalogue simply had no entry and the lookup fell
     * back to the key. Adding a reason to `@obsina/domain` without a sentence
     * here is now a compile error, which is where that class of leak belongs.
     */
    readonly reasons: Readonly<Record<CoverageReason, string>>;
  };
  readonly error: {
    readonly title: string;
    readonly body: string;
    readonly retry: string;
    /**
     * Stated when a refresh fails while a graph is already drawn. The drawn map
     * is authorized and still true, so it stays; this line says only that the
     * requested reload did not arrive.
     */
    readonly reloadFailed: string;
  };
  readonly unavailable: {
    readonly title: string;
    readonly body: string;
  };
  readonly unauthorized: {
    readonly title: string;
    readonly body: string;
    readonly reload: string;
  };
}
